import React, { useEffect, useState, useRef } from 'react';
import shortid from 'shortid';
import pell from 'pell';
import PropTypes from 'prop-types';
import logEvent from '@zr/utilities/logEvent';
import sanitize from '@zr/utilities/xss';

import 'pell/dist/pell.min.css';
import './RichTextareaField.less';

import { toPlaintext, toHtml } from '@zr/utilities/htmlPlainText';

const uListIcon = '<svg width="40" height="40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="6" fill="#F6F7F8"/><path d="M16 14h13M16 20h13M16 26h13M11 14h.01M11 20h.01M11 26h.01" stroke="#515659" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const oListIcon = '<svg width="40" height="40" fill="none" xmlns="http://www.w3.org/2000/svg"><rect width="40" height="40" rx="6" fill="#F6F7F8"/><path d="M16 14h13M16 20h13" stroke="#515659" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M10.095 18.979h1.294v-6.185H10.1l-1.598 1.11v1.166l1.517-1.055h.077v4.964zM8.5 23.287h1.112c0-.56.366-.953.95-.953.559 0 .884.373.884.842 0 .397-.155.659-.869 1.354l-2.009 1.94v.807h4.133v-.965h-2.517v-.071l1.19-1.136c.954-.905 1.247-1.365 1.247-2.036 0-.965-.778-1.668-1.992-1.668-1.255 0-2.128.77-2.128 1.886z" fill="#515659"/><path d="M16 26h13" stroke="#515659" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

const isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined';
const isIE11 = isBrowser && !!(window.MSInputMethodContext && document.documentMode);

export const RichTextareaField = ({
  children,
  onChange,
  value,
  className,
  isResetting,
  onReset,
  input,
  richTextEditor,
  ...props
}) => {
  if (isIE11 || !richTextEditor) {
    logEvent('textarea_view', {
      rich_text: 0,
    });
    RichTextareaField.displayName = 'textarea';
    return (
      <textarea
        {...props}
        {...input}
      />
    );
  }

  const [textareaId] = useState(`RichTextareaField_${shortid.generate()}`);
  const [pellId] = useState(`PellEditor_${shortid.generate()}`);
  const valueRef = useRef(value);
  const editorRef = useRef();

  useEffect(() => {
    // each call to pell.init appends a new textarea, so make sure it only runs once.
    if (editorRef.current) {
      return;
    }
    // initialize pell
    editorRef.current = pell.init({
      element: document.getElementById(pellId),
      defaultParagraphSeparator: 'p',
      onChange: (html) => {
        if (html !== '') {
          // converts html to plain text on change which updates value for textarea
          onChange(toPlaintext(html));
        } else {
          // sets default of empty pell editor to p br p for design purposes
          editorRef.current.content.innerHTML = '<p><br><p>';
        }
      },
      // limit to actions specificed in array
      actions: [
        { name: 'ulist', icon: uListIcon },
        { name: 'olist', icon: oListIcon },
      ],
    });

    logEvent('textarea_view', {
      rich_text: 1,
    });

    // DOM manipulation of pell editor so actionbar is below content for accessibility
    const $pellEditor = $(editorRef.current);
    const $pellActionBar = $pellEditor.find('.pell-actionbar');
    $pellActionBar.next().insertBefore($pellActionBar);

    $pellActionBar.on('click', (e) => {
      const buttonTitle = $(e.target).closest('button').attr('title');
      if (buttonTitle) {
        logEvent('rich_text_click', {
          type: buttonTitle,
        });
      }
    });

    // If value passed to component, inject value into editor
    if (valueRef.current) {
      editorRef.current.content.innerHTML = sanitize(toHtml(valueRef.current));
    } else {
      editorRef.current.content.innerHTML = '<p><br><p>';
    }
  }, [onChange, pellId]);

  useEffect(() => {
    // handle form reset when isResetting is true or some string
    if (isResetting !== false) {
      if (isResetting === true || isResetting === '') {
        editorRef.current.content.innerHTML = '<p><br><p>';
      } else {
        editorRef.current.content.innerHTML = sanitize(toHtml(isResetting));
      }
      onReset();
    }
  }, [isResetting, onReset]);

  return (
    <>
      <div id={pellId} className="pell" />
      <textarea id={textareaId} value={value} className={`richtext_hidden_field ${className}`} {...props} readOnly />
    </>
  );
};

RichTextareaField.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
  ]),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  className: PropTypes.string,
  onReset: PropTypes.func,
  isResetting: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.bool,
  ]),
  richTextEditor: PropTypes.bool,

  input: PropTypes.shape({
    /** Returns the name prop that you passed in. */
    name: PropTypes.string,
    /**
     * The value of this form field. It will default to `''` to ensure that the
     * input is controlled.
    */
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool,
    ]),
  }).isRequired,
};

RichTextareaField.defaultProps = {
  children: null,
  value: '',
  className: '',
  onReset: () => {},
  isResetting: false,
  richTextEditor: true,
};

RichTextareaField.displayName = 'RichTextareaField';

export default RichTextareaField;
